import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Shield, Lock, Eye, Database } from 'lucide-react';
export function Security() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const badges = [{
    label: 'GDPR',
    description: 'Cumplimiento total'
  }, {
    label: 'PCI DSS',
    description: 'Cumplimiento'
  }, {
    label: 'ISO 27001',
    description: 'Seguridad certificada'
  }, {
    label: 'SOC 2',
    description: 'Tipo II'
  }];
  const metrics = [{
    value: '+2M',
    label: 'Identidades verificadas'
  }, {
    value: '99.99%',
    label: 'Uptime garantizado'
  }, {
    value: '<3s',
    label: 'Tiempo de verificación'
  }];
  return <section id="seguridad" className="py-20 md:py-32" ref={ref} data-id="element-127">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-128">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-129">
          {/* Left Column - Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.6
        }} data-id="element-130">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-6" data-id="element-131">
              Seguridad sin compromisos
            </h2>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-132">
              Nuestra arquitectura de seguridad está diseñada desde cero para
              proteger tu privacidad. Utilizamos pruebas de conocimiento cero,
              cifrado de extremo a extremo y minimización de datos para
              garantizar que tus datos personales permanezcan siempre bajo tu
              control.
            </p>

            <div className="space-y-4" data-id="element-133">
              <div className="flex items-start space-x-3" data-id="element-134">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-135">
                  <Shield size={20} className="text-teal-500" data-id="element-136" />
                </div>
                <div data-id="element-137">
                  <h4 className="font-semibold text-white mb-1" data-id="element-138">
                    Zero-Knowledge Proofs
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-139">
                    Verifica sin revelar información sensible
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-140">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-141">
                  <Lock size={20} className="text-teal-500" data-id="element-142" />
                </div>
                <div data-id="element-143">
                  <h4 className="font-semibold text-white mb-1" data-id="element-144">Cifrado E2E</h4>
                  <p className="text-sm text-gray-400" data-id="element-145">
                    Tus datos están protegidos en todo momento
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-146">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-147">
                  <Eye size={20} className="text-teal-500" data-id="element-148" />
                </div>
                <div data-id="element-149">
                  <h4 className="font-semibold text-white mb-1" data-id="element-150">
                    Transparencia Total
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-151">Auditorías regulares</p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-152">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-153">
                  <Database size={20} className="text-teal-500" data-id="element-154" />
                </div>
                <div data-id="element-155">
                  <h4 className="font-semibold text-white mb-1" data-id="element-156">
                    Minimización de Datos
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-157">
                    Solo recopilamos lo estrictamente necesario
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Badges & Metrics */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: 30
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-8" data-id="element-158">
            {/* Compliance Badges */}
            <div data-id="element-159">
              <h3 className="text-xl font-display font-semibold text-white mb-4" data-id="element-160">
                Certificaciones y cumplimiento
              </h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-161">
                {badges.map((badge, index) => <motion.div key={badge.label} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={isInView ? {
                opacity: 1,
                scale: 1
              } : {
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.1
              }} className="bg-navy-800 border border-teal-500/20 rounded-lg p-4 text-center hover:border-teal-500/40 transition-colors" data-id="element-162">
                    <div className="text-2xl font-display font-bold text-teal-500 mb-1" data-id="element-163">
                      {badge.label}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-164">
                      {badge.description}
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}